import { useState, memo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { MapPin, Phone, Mail, Clock, Globe, Building2, ExternalLink } from 'lucide-react'
import { ContactCTA } from '@/components/sections'
import { LocationsMap } from '@/components/LocationsMap'
import { getStats, locations } from '@/data/locations'
import doveSiamoImg from '@/assets/images/sections/DoveSiamo.jpg'

type FilterType = 'all' | 'italy' | 'spain' | 'other'

// Locations Badge
const LocationsBadge = memo(function LocationsBadge({ text }: { text: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-full shadow-lg"
    >
      <MapPin className="w-5 h-5" />
      <span className="font-semibold tracking-wide">{text}</span>
    </motion.div>
  )
})

// Stats Card Component
const StatCard = memo(function StatCard({
  icon: Icon,
  value,
  label,
  delay,
  color,
}: {
  icon: typeof MapPin
  value: number
  label: string
  delay: number
  color: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className="relative group"
    >
      <div className="relative overflow-hidden rounded-2xl bg-white/80 backdrop-blur-sm border border-white/50 p-6 shadow-xl hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1 text-center">
        {/* Background gradient orb */}
        <div
          className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br ${color} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`}
        />

        {/* Icon */}
        <div
          className={`relative mx-auto w-14 h-14 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-4 shadow-lg`}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>

        {/* Value */}
        <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">{value}</div>

        {/* Label */}
        <div className="text-sm font-medium text-gray-600">{label}</div>
      </div>
    </motion.div>
  )
})

export function WhereWeAre() {
  const { t, i18n } = useTranslation(['locations', 'common'])
  const [mapFilter, setMapFilter] = useState<FilterType>('all')
  const stats = getStats()

  const location = {
    name: t('locations:headquarters.name'),
    address: 'Via XX Settembre 52',
    city: '00042 - Anzio (RM)',
    phone: '+39 06 0000 0000',
    email: 'info@bfwellness.it',
    hours: i18n.language === 'en' ? 'Mon - Fri: 9:00 AM - 6:00 PM' : 'Lun - Ven: 9:00 - 18:00',
  }

  const filterButtons: { key: FilterType; label: string; count: number }[] = [
    { key: 'all', label: i18n.language === 'en' ? 'All' : 'Tutte', count: stats.total },
    { key: 'italy', label: i18n.language === 'en' ? 'Italy' : 'Italia', count: stats.italy },
    { key: 'spain', label: i18n.language === 'en' ? 'Spain' : 'Spagna', count: locations.filter(l => l.country === 'spain').length },
    { key: 'other', label: i18n.language === 'en' ? 'Other' : 'Altre', count: locations.filter(l => l.country === 'other').length },
  ]

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('locations:meta.title')}</title>
        <meta name="description" content={t('locations:meta.description')} />
      </Helmet>

      {/* Hero Section with Parallax Effect */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={doveSiamoImg}
            alt="BF Wellness Locations"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-64 h-64 rounded-full bg-white/5"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.2, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }}
              transition={{
                duration: 8 + i * 2,
                repeat: Infinity,
                delay: i * 1.5,
              }}
              style={{
                left: `${20 + i * 15}%`,
                top: `${10 + i * 20}%`,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative container-custom py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <LocationsBadge text={t('hero.badge')} />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-white mt-6 mb-6"
            >
              {t('locations:hero.title')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8"
            >
              {t('locations:hero.subtitle')}
            </motion.p>

            {/* Keywords */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {['ITALIA', 'SPAGNA', 'EUROPA'].map((word) => (
                <span
                  key={word}
                  className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold text-sm"
                >
                  {word}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 rounded-full bg-white/60" />
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 opacity-50 blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-teal-100 to-emerald-100 opacity-50 blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('locations:presence.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('locations:presence.description')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <StatCard
              icon={MapPin}
              value={stats.italy}
              label={t('locations:presence.italy')}
              delay={0}
              color="from-emerald-500 to-teal-500"
            />
            <StatCard
              icon={Globe}
              value={stats.international}
              label={i18n.language === 'en' ? 'International' : 'Estero'}
              delay={0.1}
              color="from-teal-500 to-cyan-500"
            />
            <StatCard
              icon={Building2}
              value={stats.total}
              label={i18n.language === 'en' ? 'Total' : 'Totale'}
              delay={0.2}
              color="from-emerald-600 to-teal-600"
            />
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {i18n.language === 'en' ? 'Our Locations' : 'Le Nostre Sedi'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              {i18n.language === 'en'
                ? 'Explore all our wellness centers across Italy and Europe'
                : 'Esplora tutti i nostri centri wellness in Italia e in Europa'}
            </p>

            {/* Filter Buttons */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {filterButtons.map((btn, index) => (
                <motion.button
                  key={btn.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  onClick={() => setMapFilter(btn.key)}
                  className={`relative flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-5 sm:py-3 rounded-full transition-all duration-300 ${
                    mapFilter === btn.key
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="text-sm sm:text-base font-semibold">{btn.label}</span>
                  <span
                    className={`text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full ${
                      mapFilter === btn.key
                        ? 'bg-white text-emerald-600'
                        : 'bg-gray-800 text-white'
                    }`}
                  >
                    {btn.count}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border border-gray-200"
          >
            <LocationsMap className="h-[350px] sm:h-[450px] md:h-[600px]" filter={mapFilter} />
          </motion.div>
        </div>
      </section>

      {/* Headquarters Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t('locations:headquarters.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {t('locations:headquarters.description')}
              </p>

              {/* Info Card */}
              <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white border border-gray-100 p-4 sm:p-6 shadow-lg">
                {/* Background decoration */}
                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 opacity-10 blur-2xl" />

                <div className="relative space-y-3 sm:space-y-5">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                      <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">{location.name}</h3>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm sm:text-base text-gray-700 font-medium">{location.address}</p>
                      <p className="text-sm sm:text-base text-gray-600">{location.city}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 flex-shrink-0" />
                    <a
                      href={`tel:${location.phone}`}
                      className="text-sm sm:text-base text-gray-700 font-medium hover:text-emerald-600 transition-colors"
                    >
                      {location.phone}
                    </a>
                  </div>

                  <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 flex-shrink-0" />
                    <a
                      href={`mailto:${location.email}`}
                      className="text-sm sm:text-base text-gray-700 font-medium hover:text-emerald-600 transition-colors break-all"
                    >
                      {location.email}
                    </a>
                  </div>

                  <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl bg-gray-50">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm sm:text-base text-gray-700">{location.hours}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2984.642852677881!2d12.6255!3d41.4485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDI2JzU0LjYiTiAxMsKwMzcnMzEuOCJF!5e0!3m2!1sit!2sit!4v1000000000000!5m2!1sit!2sit"
                  width="100%"
                  height="350"
                  className="w-full sm:h-[450px]"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="BF Wellness Location"
                />
              </div>

              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-4 -left-2 sm:-bottom-6 sm:-left-6 bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-xl border border-gray-100"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm sm:text-base font-bold text-gray-900">
                      {i18n.language === 'en' ? 'Get Directions' : 'Indicazioni'}
                    </div>
                    <a
                      href="https://maps.google.com/?q=Via+XX+Settembre+52,+Anzio"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs sm:text-sm text-emerald-600 hover:underline"
                    >
                      Google Maps
                    </a>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  )
}
